/**
 * Status rules run on the server after the write that set them off, so the
 * record on screen can move with nothing in this tab knowing. Two signals let
 * the open record re-read itself:
 *
 * - `moved`: a notification about an automatic move named the record;
 * - `expected`: a write that commonly sets a rule off just succeeded. The
 *   owner's notification may be switched off, so the open record re-reads
 *   once a little later whatever arrives.
 */
export type RecordStatusSignal =
  | { kind: "moved"; recordIds: string[] }
  | { kind: "expected" }

type Listener = (signal: RecordStatusSignal) => void

const listeners = new Set<Listener>()

export const onRecordStatusSignal = (listener: Listener) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const emit = (signal: RecordStatusSignal) => {
  listeners.forEach((listener) => listener(signal))
}

interface AutomaticMoveNotice {
  data?: {
    automation?: unknown
    lead_id?: string | null
    project_id?: string | null
  } | null
}

/** Called for every new notification; only a status rule's move is passed on. */
export const announceAutomaticMove = (
  notification: AutomaticMoveNotice | null | undefined
) => {
  const data = notification?.data
  if (!data?.automation) return
  const recordIds = [data.lead_id, data.project_id]
    .filter((id): id is string => !!id)
    .map(String)
  if (recordIds.length) emit({ kind: "moved", recordIds })
}

export const expectAutomaticMove = () => emit({ kind: "expected" })

/** Passes a write's response through, flagging it when it succeeded. */
export const mayMoveStatus = <R extends { ok?: boolean } | null | undefined>(
  response: R
): R => {
  if (response?.ok) expectAutomaticMove()
  return response
}

/** Awaits a write and flags it when it succeeded. */
export const afterStatusWrite = <R extends { ok?: boolean } | null | undefined>(
  response: R | Promise<R>
): Promise<R> => Promise.resolve(response).then(mayMoveStatus)
